import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/components/site/seo-copy";
import { Page, PageHead, Cta, SectionHead } from "@/components/site/Page";
import { Link } from "@tanstack/react-router";


export const Route = createFileRoute("/how-it-works")({
  head: () => pageHead("/how-it-works"),
  component: HowItWorks,
});

const steps = [
  {
    n: "01",
    t: "An employer posts a sales job",
    d: "Role, pay, product, location. Free, under 5 minutes.",
  },
  {
    n: "02",
    t: "Salespeople apply",
    d: "One profile, one click, apply to any role.",
  },
  {
    n: "03",
    t: "Candidates record their pitch",
    d: "60 seconds. No formatting games. Just them, talking.",
  },
  {
    n: "04",
    t: "The employer listens first",
    d: "Pitch before CV. Shortlist in minutes, not days.",
  },
  {
    n: "05",
    t: "The employer hires directly",
    d: "They make the call. Every time.",
  },
];

function HowItWorks() {
  return (
    <Page>
      <PageHead
        eyebrow="How CLOSDD works"
        title="How hiring from a pitch works"
        intro="Marketing changed. Product changed. Ops changed. The way companies hire engineers changed. Sales drives growth more than almost any other function in a business, and it's still hired the same way it was thirty years ago."
      >
        <Cta to="/post-job">Post a role free</Cta>
        <Link
          to="/find-jobs"
          className="inline-flex items-center rounded-full border border-white/30 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-white/10"
        >
          Find jobs
        </Link>
      </PageHead>

      <section className="mx-auto w-full max-w-[1280px] px-5 pb-10">
        <div className="rounded-[36px] bg-purple-strong px-8 py-12 lg:px-16 lg:py-16">
          <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
            <div>
              <p className="text-[12px] font-bold uppercase tracking-[0.24em] text-mint-soft">
                The problem
              </p>
              <h2 className="mt-5 font-serif text-[25px] leading-[1.0] tracking-[-0.03em] text-white sm:text-[34px] lg:text-[38px]">
                The most important hire for growth, and it's still being made off a piece of paper.
              </h2>
            </div>
            <div className="flex flex-col justify-center gap-6 text-[17px] leading-[1.7] text-white/70">
              <p>
                A CV. Keywords. A recruiter guessing. Some of the best salespeople alive can't write a great CV. That's never been the same skill, and it never will be.
              </p>
              <p>
                But the ATS doesn't know that. It filters people out before a human ever hears them speak.
              </p>
              <p className="text-white">
                We think that's backwards. So we built the fix.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-5 pb-10">
        <div className="mb-8">
          <SectionHead
            eyebrow="The model"
            title="How it works"
            intro="Employers post a role. Salespeople answer with a 60-second pitch instead of a resume. Employers listen before they read, shortlist in minutes, and hire directly. No recruiter. No middle layer. No algorithm deciding who gets seen."
          />
        </div>
        <div className="relative">
          <div className="absolute left-[28px] top-0 bottom-0 hidden w-px bg-purple-strong/10 lg:block" />
          <div className="grid gap-4">
            {steps.map((step) => (
              <article
                key={step.n}
                className="relative grid items-center gap-6 rounded-[28px] border border-purple-strong/10 bg-white/70 p-7 lg:grid-cols-[120px_1fr] lg:p-9"
              >
                <div className="relative">
                  <p className="font-serif text-[38px] leading-none text-purple-strong lg:text-[46px]">
                    {step.n}
                  </p>
                  <div className="absolute left-[40px] top-1/2 hidden h-2 w-2 -translate-y-1/2 rounded-full bg-purple-mid lg:block" />
                </div>
                <div>
                  <h3 className="font-serif text-[20px] leading-tight tracking-[-0.02em] text-ink sm:text-[24px]">
                    {step.t}
                  </h3>
                  <p className="mt-2 text-[17px] leading-[1.6] text-ink/60">
                    {step.d}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-5 pb-10">
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-[28px] bg-purple-soft p-8 lg:p-10">
            <SectionHead
              eyebrow="For employers"
              title="Stop guessing from paper."
              intro="You post a sales role. 200 people apply. You open CV after CV, chase candidates, book interviews, only to find out half of them can't hold a conversation."
            />
            <p className="mt-6 text-[17px] leading-[1.6] text-ink/65">
              Hear them before you ever book a call.
            </p>
            <div className="mt-8">
              <Link
                to="/post-job"
                className="inline-flex items-center rounded-full bg-purple-strong px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-white transition-colors hover:bg-purple-mid"
              >
                Post a role free
              </Link>
            </div>
          </div>
          <div className="rounded-[28px] bg-purple-strong p-8 lg:p-10">
            <p className="text-[12px] font-bold uppercase tracking-[0.24em] text-mint-soft">
              For salespeople
            </p>
            <h2 className="mt-5 font-serif text-[25px] leading-[1.0] tracking-[-0.03em] text-white sm:text-[34px]">
              Skip the ATS. Prove it instead of writing it.
            </h2>
            <div className="mt-6 space-y-4 text-[17px] leading-[1.65] text-white/70">
              <p>
                If you can sell, you get the shot. Not the person who spent three hours in a CV template.
              </p>
              <p>
                Experience isn't everything. A great salesperson with two years can beat an average one with ten. Free to apply.
              </p>
            </div>
            <div className="mt-8">
              <Link
                to="/find-jobs"
                className="inline-flex items-center rounded-full bg-white px-8 py-4 text-[13px] font-bold uppercase tracking-[0.16em] text-purple-strong transition-colors hover:bg-mint-strong"
              >
                Pitch yourself
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1280px] px-5 pb-16 lg:pb-24">
        <div className="rounded-[36px] bg-purple-strong px-8 py-12 text-center lg:px-16 lg:py-20">
          <p className="font-serif text-[28px] leading-[1.0] tracking-[-0.03em] text-white sm:text-[42px] lg:text-[52px]">
            Closdd.
          </p>
          <p className="mx-auto mt-5 max-w-[700px] text-[17px] leading-[1.6] text-white/75 sm:text-[20px]">
            Candidates don't just apply. They pitch.
          </p>
        </div>
      </section>
    </Page>
  );
}
